import { useRef, useState } from 'react';
import { Accordion } from '@ds';
import { AccordionSize, AccordionVariant } from '@ds/elements/Accordion/Accordion.types';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import CheckmarkCircle from '@icons/checkmark-circle.svg';
import DashCircle from '@icons/dash-circle-2.svg';
import DocumentsEmpty from '@icons/documents-empty.svg';
import Download from '@icons/download.svg';
import styles from './Passports.module.scss';
import { createDownloadHandler, sortPassports } from '../ImmigrationStatus.service';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { useGetPassportDetails } from 'src/shared/queries/useGetPassportDetails';
import { FallbackPlaceholderText } from 'src/utils/constants/common';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import { displayYesNo, getCountryName } from 'src/utils/services/common';

const Passports = (details: {
  beneId: number | undefined;
  dependentId: number | undefined;
  showActiveOnly: boolean;
}) => {
  const { data: countries } = useGetCountries();
  const { data: unsortedPassports } = useGetPassportDetails(
    details.dependentId ? undefined : details.beneId,
    details.dependentId
  );
  const sortedPassports = sortPassports(unsortedPassports);
  const [isProcessing, setIsProcessing] = useState(false);
  const toastRef = useRef<IToastr>(null);

  return (
    <div className={styles['immigration-status-passports']}>
      <h3 className="my-2">Passports</h3>

      {sortedPassports && (
        <Accordion
          activeIndex={null}
          className={styles['passports-accordion']}
          variant={AccordionVariant.Transparent}
          size={AccordionSize.Large}
          highlightActive={false}
          multiple={true}
          tabs={sortedPassports
            .filter((passport) => !details.showActiveOnly || !passport.isInactive)
            .map((passport) => ({
              header: (
                <div className={`flex flex-wrap ${styles['passports-accordion__header']} mt-2 mb-2`}>
                  <div className="flex-shrink-0 p-4">
                    <img src={passport.isInactive ? DashCircle : CheckmarkCircle} />
                  </div>
                  <div className={`flex flex-col sm:w-auto xl:flex-row ${styles['header-cols']}`}>
                    <div className={`py-2 px-4 ${styles['header-col']}`}>
                      <label>Country / Location:</label>
                      <p>
                        {(countries && getCountryName(countries, passport.country)) ||
                          FallbackPlaceholderText.SingleDash}
                      </p>
                    </div>
                    <div className={`py-2 px-4 ${styles['header-col']}`}>
                      <label className="">Passport Number:</label>
                      <p>{passport.passportNumber || FallbackPlaceholderText.SingleDash}</p>
                    </div>
                    <div className={`py-2 px-4 ${styles['header-col']}`}>
                      <label className="">Expiration Date:</label>
                      <p>
                        {passport.passportExpirationDate == null
                          ? FallbackPlaceholderText.SingleDash
                          : getFormattedDate(passport.passportExpirationDate as string, UserDateFormat.get())}
                      </p>
                    </div>
                  </div>
                </div>
              ),
              body: (
                <div
                  className={`flex flex-wrap flex-col md:flex-row mb-4 md:p-5 ${styles['passports-accordion__body']}`}
                >
                  <div className={`flex flex-col items-center w-full`}>
                    {passport.documents?.map((doc) => (
                      <div
                        key={`document-${passport.docId}`}
                        className={` w-full ${styles['attachment']}`}
                        onClick={() => {
                          if (isProcessing) return;
                          createDownloadHandler({
                            doc,
                            beneId: passport.beneId ?? details.beneId,
                            toastRef,
                            setIsProcessing,
                          })();
                        }}
                      >
                        <span className={`${styles['name']}`}>
                          <img src={DocumentsEmpty} className={`${styles['document-icon']}`} />
                          <span>{doc.docName}</span>
                        </span>
                        <img src={Download} className={`${styles['download-icon']}`} />
                      </div>
                    ))}
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Issued Date</label>
                      <p>
                        {passport.issuedDate == null
                          ? FallbackPlaceholderText.TripleDash
                          : getFormattedDate(passport.issuedDate as string, UserDateFormat.get())}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Issuing Authority</label>
                      <p>
                        {(countries && getCountryName(countries, passport.passportIssuedCountry)) ||
                          FallbackPlaceholderText.TripleDash}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Are there at least 2 blank pages?</label>
                      <p>{displayYesNo(passport.hasEnoughBlanks)}</p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Issuing Authority</label>
                      <p>{passport.issuingAuthority || FallbackPlaceholderText.TripleDash}</p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Issued at State / Province</label>
                      <p>{passport.issuedState || FallbackPlaceholderText.TripleDash}</p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Is it a biometric passport?</label>
                      <p>{displayYesNo(passport.isBiometric)}</p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Type of Passport</label>
                      <p>{passport.displayPassportType || FallbackPlaceholderText.TripleDash}</p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Issued at City</label>
                      <p>{passport.issuedCity || FallbackPlaceholderText.TripleDash}</p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Is it an emergency or temporary passport?</label>
                      <p>{displayYesNo(passport.isTemporary)}</p>
                    </div>
                  </div>
                  <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
                    <div>
                      <label>Location of Passport</label>
                      <p>{passport.displayLocationOfPassport || FallbackPlaceholderText.TripleDash}</p>
                    </div>
                  </div>
                </div>
              ),
            }))}
        />
      )}
    </div>
  );
};

export default Passports;
